/* eslint-disable jsx-a11y/control-has-associated-label */
import { useEffect, useState } from 'react';
import {
  RxArrowRight,
  RxHamburgerMenu,
  RxCross1,
  RxArrowLeft,
} from 'react-icons/rx';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { buildTalkToSalesLink } from 'src/utils/talkToSales';

import { ReactComponent as StudioLogo } from '../images/Studio-Dark.svg';
import styles from '../pages/PublicPage.module.css';

type HeaderProps = {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (value: boolean) => void;
  isRoot: boolean;
};

/* 
  Shared nav header on public pages
*/
export const Header = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isRoot,
}: HeaderProps) => (
  <header className={styles.header} data-testid="auth-header">
    <Link
      to="/"
      className={styles['studio-logo']}
      aria-label="Ouster Studio Home"
      data-testid="auth-logo-link"
    >
      <StudioLogo width={258} />
    </Link>

    <nav className={styles.nav}>
      <Link
        to="https://ouster.com/products/software/ouster-studio"
        target="_blank"
        className={styles['text-button']}
        data-testid="auth-download-link"
      >
        Download Ouster Studio
      </Link>
      <Link
        to={buildTalkToSalesLink('Login or Signup Menu')}
        target="_blank"
        rel="noopener noreferrer"
        className={styles['text-button']}
        data-testid="auth-contact-link"
      >
        Contact Sales
      </Link>
    </nav>

    {isRoot && (
      <div
        className={styles['action-buttons']}
        data-testid="auth-action-buttons"
      >
        <Link
          reloadDocument
          to="/login"
          className={styles['secondary-button']}
          data-testid="auth-login-link"
        >
          Log in
        </Link>
        <Link
          to="/sign_up"
          className={styles['primary-button']}
          data-testid="auth-signup-link"
        >
          Sign up for free <RxArrowRight />
        </Link>
      </div>
    )}
    <div
      className={styles['mobile-buttons-container']}
      data-testid="auth-mobile-buttons"
    >
      {isMobileMenuOpen ? (
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(false)}
          className={styles['mobile-close-button']}
          data-testid="auth-mobile-close"
        >
          <RxCross1 size={28} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(true)}
          className={styles['mobile-open-button']}
          data-testid="auth-mobile-open"
        >
          <span>
            <RxHamburgerMenu size={28} />
          </span>
        </button>
      )}
    </div>
  </header>
);

/* 
  shared back button on public pages
*/
export const NavBackButton = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <button
      type="button"
      className={styles['text-button']}
      onClick={handleBack}
      data-testid="auth-back-button"
    >
      <RxArrowLeft />
      Back
    </button>
  );
};

/* 
  Mobile menu panel
*/

const MobileMenuPanel = ({
  isMobileMenuOpen,
  isRoot,
}: {
  isMobileMenuOpen: boolean;
  isRoot: boolean;
}) => (
  <section
    className={`${styles['mobile-menu-container']} ${
      isMobileMenuOpen ? styles['panel-open'] : ''
    }`}
    data-testid="auth-mobile-menu"
  >
    <nav className={styles['mobile-nav']} data-testid="auth-mobile-nav">
      <Link
        to="https://ouster.com/products/software/ouster-studio"
        target="_blank"
        className={styles['text-button']}
        data-testid="auth-mobile-download-link"
      >
        Download Ouster Studio
      </Link>
      <Link
        to={buildTalkToSalesLink('Login or Signup Menu')}
        target="_blank"
        rel="noopener noreferrer"
        className={styles['text-button']}
        data-testid="auth-mobile-contact-link"
      >
        Contact Sales
      </Link>
    </nav>

    {isRoot && (
      <div
        className={styles['mobile-action-buttons']}
        data-testid="auth-mobile-action-buttons"
      >
        <Link
          to="/sign_up"
          className={styles['primary-button']}
          data-testid="auth-mobile-signup-link"
        >
          Sign up for free <RxArrowRight />
        </Link>
        <Link
          reloadDocument
          to="/login"
          className={styles['secondary-button']}
          data-testid="auth-mobile-login-link"
        >
          Log in
        </Link>
      </div>
    )}
  </section>
);

export default function AuthLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <div className={styles.page} data-testid="auth-layout-root">
      <Header
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        isRoot={location.pathname === '/'}
      />
      <main data-testid="auth-layout-main">
        <Outlet />
        <MobileMenuPanel
          isMobileMenuOpen={isMobileMenuOpen}
          isRoot={location.pathname === '/'}
        />
      </main>
    </div>
  );
}
